import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Autocomplete,
  TextField,
  FormControlLabel,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Typography,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { PaymentRailsApi } from '@/api/clients/payment-rails/payment-rails.api';
import {
  useGetPaymentRailCatalog,
  expandCatalogOptions,
  getPaymentRailDisplayName,
} from '@/api/hooks/payment-rails/useGetPaymentRailCatalog';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import CpdActionButton from '@/components/ui/CpdActionButton';
import useFormValidation from '@/hooks/useFormValidation';

const ALL_ENTITY_TYPES = ['Individual', 'Business'];

export default function PaymentRailFormDialog({
  open,
  onClose,
  paymentRailSettingId,
  rail,
  existingKeys,
  onSaved,
}) {
  const { t } = useTranslation();
  const isEdit = !!rail;
  const { catalog, loading: catalogLoading } = useGetPaymentRailCatalog();
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  const [selectedOptionKey, setSelectedOptionKey] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [entityTypes, setEntityTypes] = useState([...ALL_ENTITY_TYPES]);
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  const allOptions = useMemo(() => expandCatalogOptions(catalog), [catalog]);

  const availableOptions = useMemo(() => {
    if (isEdit) return allOptions;
    return allOptions.filter((opt) => !existingKeys.has(opt.key));
  }, [allOptions, existingKeys, isEdit]);

  const selectedOption = useMemo(
    () => allOptions.find((o) => o.key === selectedOptionKey) || null,
    [allOptions, selectedOptionKey],
  );

  const allowedWorkflows = selectedOption?.workflows || [];

  useEffect(() => {
    if (!open) return;
    clearAllErrors();
    if (isEdit) {
      const key =
        rail.subType == null
          ? `${rail.method}`
          : `${rail.method}-${rail.subType}`;
      setSelectedOptionKey(key);
      setWorkflows(rail.workflows || []);
      setEntityTypes(rail.entityTypes || []);
      setIsEnabled(!!rail.isEnabled);
    } else {
      setSelectedOptionKey(null);
      setWorkflows([]);
      setEntityTypes([...ALL_ENTITY_TYPES]);
      setIsEnabled(true);
    }
  }, [open, isEdit, rail]);

  const handleSave = async () => {
    clearAllErrors();
    if (!selectedOption) {
      setBackendErrors({
        errors: [
          { path: 'method', msg: t('paymentRails.fields.railRequired') },
        ],
      });
      return;
    }
    if (workflows.length === 0) {
      setBackendErrors({
        errors: [
          {
            path: 'workflows',
            msg: t('paymentRails.fields.workflowsRequired'),
          },
        ],
      });
      return;
    }
    if (entityTypes.length === 0) {
      setBackendErrors({
        errors: [
          {
            path: 'entity_types',
            msg: t('paymentRails.fields.entityTypesRequired'),
          },
        ],
      });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await PaymentRailsApi.update(rail.id, {
          workflows,
          entity_types: entityTypes,
          is_enabled: isEnabled,
        });
      } else {
        await PaymentRailsApi.create({
          payment_rail_setting_id: paymentRailSettingId,
          method: selectedOption.method,
          sub_type: selectedOption.subType,
          workflows,
          entity_types: entityTypes,
          is_enabled: isEnabled,
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      const code = err.response?.data?.error_code;
      if (code === 'PAYMENT_RAIL_DUPLICATE') {
        setBackendErrors({
          errors: [
            {
              path: 'method',
              msg: t('paymentRails.errors.duplicate'),
            },
          ],
        });
      } else if (code === 'PAYMENT_RAIL_INVALID_ENTITY_TYPE') {
        setBackendErrors({
          errors: [
            {
              path: 'entity_types',
              msg: t('paymentRails.errors.invalidEntityType'),
            },
          ],
        });
      } else {
        setBackendErrors(err.response?.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth='sm'
      fullWidth>
      <DialogTitle>
        {t(isEdit ? 'paymentRails.editTitle' : 'paymentRails.addTitle')}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 1 }}>
          <FormErrorSummary errors={generalErrors} />

          {isEdit ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant='caption' color='text.secondary'>
                {t('paymentRails.fields.rail')}
              </Typography>
              <Typography variant='body1'>
                {getPaymentRailDisplayName(catalog, rail.method, rail.subType)}
              </Typography>
            </Box>
          ) : (
            <Autocomplete
              options={availableOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.key === v.key}
              value={selectedOption}
              loading={catalogLoading}
              onChange={(_, newValue) => {
                setSelectedOptionKey(newValue?.key || null);
                // reset workflows when rail changes
                setWorkflows([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='standard'
                  label={t('paymentRails.fields.rail')}
                  error={!!fieldErrors.method}
                  helperText={fieldErrors.method}
                />
              )}
              sx={{ mb: 2 }}
            />
          )}

          <Box sx={{ mb: 2 }}>
            <Typography
              variant='subtitle2'
              color={fieldErrors.workflows ? 'error' : 'text.primary'}
              sx={{ mb: 1, fontWeight: 600 }}>
              {t('paymentRails.fields.workflows')}
            </Typography>
            <ToggleButtonGroup
              value={workflows}
              onChange={(_, newValue) => setWorkflows(newValue)}
              aria-label={t('paymentRails.fields.workflows')}
              disabled={!selectedOption}
              fullWidth>
              {allowedWorkflows.map((w) => (
                <ToggleButton key={w} value={w} aria-label={w}>
                  {t(`paymentRails.workflows.${w}`, { defaultValue: w })}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {fieldErrors.workflows && (
              <FormHelperText error>{fieldErrors.workflows}</FormHelperText>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant='subtitle2'
              color={fieldErrors.entity_types ? 'error' : 'text.primary'}
              sx={{ mb: 1, fontWeight: 600 }}>
              {t('paymentRails.fields.entityTypes')}
            </Typography>
            <ToggleButtonGroup
              value={entityTypes}
              onChange={(_, newValue) => setEntityTypes(newValue)}
              aria-label={t('paymentRails.fields.entityTypes')}
              fullWidth>
              {ALL_ENTITY_TYPES.map((et) => (
                <ToggleButton key={et} value={et} aria-label={et}>
                  {t(`paymentRails.entityTypes.${et}`, { defaultValue: et })}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {fieldErrors.entity_types && (
              <FormHelperText error>{fieldErrors.entity_types}</FormHelperText>
            )}
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                color='primary'
              />
            }
            label={t('paymentRails.fields.isEnabled')}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} startIcon={<Cancel />} disabled={loading}>
          {t('paymentRailSettings.cancel')}
        </Button>
        <CpdActionButton
          onClick={handleSave}
          loading={loading}
          startIcon={<Save sx={{ color: 'white' }} />}>
          {t('paymentRailSettings.save')}
        </CpdActionButton>
      </DialogActions>
    </Dialog>
  );
}
